import Leaderboard from "@/pages/team/Leaderboard";
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import TrialRecap from "@/pages/team/TrialRecap";
import { MessageSquareText, Trash2, UserPlus, Users } from "lucide-react";
import { api } from "@/lib/api";
import { errorMessage, startCheckout } from "@/lib/billing";
import TeamActivity from "./team/TeamActivity";
import { ROLE_HINTS, ROLE_LABELS, useTeam } from "@/lib/team";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DeviceStatus, { TeamReady } from "@/pages/team/DeviceStatus";
import NudgeButton from "@/pages/team/NudgeButton";
import MemberAccess from "@/pages/team/MemberAccess";
import HideQueueToggle from "@/pages/team/HideQueueToggle";
import MemberQueueSwitch from "@/pages/team/MemberQueueSwitch";
import Workload from "@/pages/team/Workload";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function RolePicker({ value, onChange, testid }) {
  return (
    <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1" role="radiogroup" data-testid={testid}>
      {Object.keys(ROLE_LABELS).map((r) => (
        <button key={r} type="button" role="radio" aria-checked={value === r} onClick={() => onChange(r)} data-testid={`${testid}-${r}`}
          className={`rounded-md px-1.5 py-1.5 text-[11px] font-semibold transition-colors ${value === r ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
          {ROLE_LABELS[r]}
        </button>
      ))}
    </div>
  );
}

export const duration = (s) => {
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h${m % 60 ? ` ${m % 60}m` : ""}`;
  return `${Math.round(h / 24)} days`;
};

function GoalInput({ m, onChanged }) {
  const [v, setV] = useState(m.weekly_goal ? String(m.weekly_goal) : "");
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/team/${m.id}/goal`, { goal: Number(v) || 0 });
      toast.success(Number(v) ? `Weekly goal set to ${Number(v)} items` : "Weekly goal removed");
      onChanged();
    } catch (err) { toast.error(errorMessage(err, "Couldn't save the goal")); }
  };
  const dirty = (Number(v) || 0) !== (m.weekly_goal || 0);
  return (
    <form onSubmit={save} className="flex items-center gap-2 text-[11px] text-slate-500" data-testid={`team-member-goal-form-${m.id}`}>
      <label htmlFor={`goal-${m.id}`}>Weekly goal</label>
      <input id={`goal-${m.id}`} type="number" min="0" max="10000" inputMode="numeric" value={v} onChange={(e) => setV(e.target.value)} placeholder="none"
        className="h-7 w-20 rounded-md border border-slate-300 px-2 font-mono text-xs" data-testid={`team-member-goal-${m.id}`} />
      <span>items</span>
      {dirty && <button type="submit" className="rounded-full bg-[#0053DA] px-2.5 py-1 font-semibold text-white" data-testid={`team-member-goal-save-${m.id}`}>Save</button>}
    </form>
  );
}

function TextInvite({ m }) {
  const site = window.location.origin;
  const body = `Hi! I added you to my SnapList team.\n1. On your phone, open ${site}/login\n2. Sign in with ${m.email} (Google or email code)\n3. Add it to your home screen: iPhone Safari: Share > Add to Home Screen. Android Chrome: menu > Install app.\n${m.role === "photos" ? "Then snap and upload photos from the Create tab." : `Your queue of items to post is at ${site}/queue`}`;
  const copy = async () => { try { await navigator.clipboard.writeText(body); toast.success("Invite copied. Paste it into a text."); } catch { toast.error("Couldn't copy"); } };
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`team-member-invite-${m.id}`}>
      <a href={`sms:?&body=${encodeURIComponent(body)}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 font-semibold text-white hover:bg-emerald-700" data-testid={`team-member-text-invite-${m.id}`}>
        <MessageSquareText className="h-3 w-3" /> Text invite
      </a>
      <button type="button" onClick={copy} className="rounded-full border border-slate-300 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50" data-testid={`team-member-copy-invite-${m.id}`}>Copy invite</button>
      <span className="text-slate-500">Opens your Messages app with sign-in and setup steps.</span>
    </div>
  );
}

function TrialBanner({ team }) {
  const days = Math.max(0, Math.ceil((new Date(team.trial_ends_at) - Date.now()) / 86400000));
  const buy = (key) => startCheckout(key).catch((e) => toast.error(errorMessage(e, "Couldn't start checkout")));
  return (
    <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="team-trial-banner">
      <p className="font-semibold" data-testid="team-trial-days">{team.trial_grace ? "Bonus day: your free week ended, here's 1 more day" : `Team free week: ${days} day${days === 1 ? "" : "s"} left`}</p>
      <p className="mt-0.5">Includes {team.seats} assistants. When it ends, you and your assistants lose access until you pick a plan.</p>
      {team.grace_code && <p className="mt-1.5 rounded-md bg-amber-100 px-2 py-1 font-semibold text-amber-900" data-testid="team-grace-code">Code <span className="font-mono">{team.grace_code}</span>: $10 off your first Team payment, applied for you at checkout. Valid until {new Date(team.grace_code_expires_at).toLocaleDateString()}.</p>}
      <p className="mt-2 font-semibold">Your free week so far</p>
      <TrialRecap recap={team.trial_recap} />
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" onClick={() => buy(team.lookup_key)} className="rounded-full bg-[#0053DA] px-3 py-1 font-semibold text-white" data-testid="team-trial-keep-monthly">Keep Team · ${(team.price_cents / 100).toFixed(2)}/mo</button>
        <button type="button" onClick={() => buy(team.yearly_lookup_key)} className="rounded-full border border-[#0053DA] px-3 py-1 font-semibold text-[#0053DA]" data-testid="team-trial-keep-yearly">Yearly · ${(team.yearly_price_cents / 100).toFixed(2)}/yr</button>
      </div>
    </div>
  );
}

function MemberRow({ m, onChanged }) {
  const setRole = async (role) => {
    try { await api.patch(`/team/${m.id}`, { role }); onChanged(); toast.success(`${m.email} is now ${ROLE_LABELS[role]}`); } catch (e) { toast.error(errorMessage(e, "Couldn't change role")); }
  };
  const [confirm, setConfirm] = useState(false);
  const remove = async () => {
    setConfirm(false);
    try { await api.delete(`/team/${m.id}`); onChanged(); toast.success("Assistant removed"); } catch (e) { toast.error(errorMessage(e, "Couldn't remove")); }
  };
  return (
    <li className="space-y-2 border-t border-slate-100 py-3" data-testid={`team-member-${m.id}`}>
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900" data-testid={`team-member-email-${m.id}`}>{m.email}</span>
        <span className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold ${m.listed_today ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}
          data-testid={`team-member-listed-today-${m.id}`}>{m.listed_today || 0} listed today</span>
        <button type="button" onClick={() => setConfirm(true)} aria-label={`Remove ${m.email}`} data-testid={`team-member-remove-${m.id}`}
          className="rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
      </div>
      {m.role !== "photos" && (
        <p className="text-[11px] text-slate-500" data-testid={`team-member-speed-${m.id}`}>
          {m.speed ? <>Speed: <b className="font-mono text-slate-800">{m.speed.batches}</b> batch{m.speed.batches === 1 ? "" : "es"} finished · about <b className="font-mono text-slate-800">{duration(m.speed.per_batch_secs)}</b> each · <b className="font-mono text-slate-800">{duration(m.speed.per_item_secs)}</b> per item</>
            : "Speed: no sent batches finished yet"}
        </p>
      )}
      {m.role !== "photos" && <Workload m={m} />}
      {m.role !== "photos" && <MemberQueueSwitch key={String(m.hide_queue)} m={m} onChanged={onChanged} />}
      <DeviceStatus m={m} />
      <MemberAccess m={m} onChanged={onChanged} />
      <NudgeButton m={m} />
      <TextInvite m={m} />
      {m.role !== "photos" && <GoalInput m={m} onChanged={onChanged} />}
      <RolePicker value={m.role} onChange={setRole} testid={`team-member-role-${m.id}`} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="w-[calc(100%-1.5rem)] max-w-sm rounded-xl" data-testid="team-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {m.email}?</AlertDialogTitle>
            <AlertDialogDescription>They lose access to your account straight away. Drafts they made stay in your Library.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-full" data-testid="team-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-full bg-red-600 text-white hover:bg-red-700" data-testid="team-remove-confirm-button">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}

export default function TeamPage() {
  const { isAssistant } = useTeam();
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("both");
  const [busy, setBusy] = useState(false);
  const [confirmSeat, setConfirmSeat] = useState(false);
  const startTrial = async () => {
    try { await api.post("/team/trial"); toast.success("Your free Team week has started. Add your assistants below."); load(); }
    catch (e) { toast.error(errorMessage(e, "Couldn't start the free week")); }
  };
  const load = () => api.get("/team", { params: { tz: new Date().getTimezoneOffset() } }).then(({ data }) => setTeam(data)).catch(() => setTeam({ is_team: false, members: [], seats: 3, max_seats: 3 }));
  useEffect(() => { if (!isAssistant) load(); }, [isAssistant]);
  if (isAssistant) return <Navigate to="/" replace />;
  if (!team) return null;

  const extraNow = team.members.length >= team.seats;
  const per = team.yearly ? "year" : "month";
  const seatPrice = `$${((team.yearly ? team.yearly_seat_cents : team.seat_cents || 500) / 100).toFixed(2)}`;
  const seatPer = team.yearly ? "/yr" : "/mo";
  const submit = (e) => {
    e.preventDefault();
    if (extraNow) setConfirmSeat(true); else add();
  };
  const add = async () => {
    setConfirmSeat(false);
    setBusy(true);
    try {
      await api.post("/team", { email: email.trim(), role });
      toast.success(`${email.trim()} can now sign in to your account`);
      setEmail(""); load();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't add assistant"));
    } finally {
      setBusy(false);
    }
  };
  const full = team.members.length >= (team.trial_active ? team.seats : team.max_seats || team.seats);
  const extras = Math.max(0, team.members.length - team.seats);
  return (
    <div className="mx-auto max-w-lg space-y-5" data-testid="team-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight"><Users className="h-6 w-6 text-[#0053DA]" /> Team</h1>
        <p className="mt-1 text-sm text-slate-600">Let a virtual assistant snap photos or list items in your account. They never see costs, profit, billing or settings.</p>
      </div>
      {!team.is_team ? (
        <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="team-upsell">
          <p className="text-sm font-semibold text-slate-900">Team plan · ${(team.price_cents / 100).toFixed(2)}/month</p>
          <p className="mt-1 text-sm text-slate-600">Everything in Pro, plus {team.seats} assistants included. More are ${((team.seat_cents || 500) / 100).toFixed(2)}/month each.</p>
          <p className="mt-1 text-sm text-slate-600" data-testid="team-yearly-note">Or pay yearly: ${(team.yearly_price_cents / 100).toFixed(2)}/year (1 month free), extra assistants ${(team.yearly_seat_cents / 100).toFixed(2)}/year each.</p>
          {team.trial_available && (
            <Button onClick={startTrial} className="mt-3 w-full rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="team-start-trial">
              Try Team free for {team.trial_days} days · no card needed
            </Button>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => startCheckout(team.lookup_key).catch((e) => toast.error(errorMessage(e, "Couldn't start checkout")))}
              className="rounded-full bg-[#0053DA] hover:bg-[#0043B0]" data-testid="team-upgrade">Get Team monthly</Button>
            <Button variant="outline" onClick={() => startCheckout(team.yearly_lookup_key).catch((e) => toast.error(errorMessage(e, "Couldn't start checkout")))}
              className="rounded-full border-[#0053DA] text-[#0053DA]" data-testid="team-upgrade-yearly">Get Team yearly · save 17%</Button>
          </div>
          <Link to="/billing" className="ml-3 text-xs text-slate-500 underline">See all plans</Link>
        </section>
      ) : (
        <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="team-manage">
          {team.trial_active && <TrialBanner team={team} />}
          <form onSubmit={submit} className="space-y-2">
            <p className="text-sm font-semibold text-slate-900">Approve an assistant's email</p>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="assistant@example.com" disabled={full} data-testid="team-email-input" />
            <RolePicker value={role} onChange={setRole} testid="team-role" />
            <p className="text-[11px] text-slate-500">{ROLE_HINTS[role]}</p>
            <Button type="submit" disabled={busy || full || !email.trim()} className="w-full rounded-full bg-[#0053DA] hover:bg-[#0043B0]" data-testid="team-add">
              <UserPlus className="mr-1.5 h-4 w-4" /> {full ? (team.trial_active ? `Free week includes ${team.seats} assistants` : `All ${team.max_seats} seats used`) : extraNow ? `Approve email · ${seatPrice}${seatPer}` : "Approve email"}
            </Button>
            <p className="text-[11px] text-slate-500">After approving, tap Text invite on their row to send them the link and setup steps.</p>
          </form>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-500" data-testid="team-seat-count">
            {team.members.length} assistant{team.members.length === 1 ? "" : "s"} · {team.seats} included{extras ? ` · ${extras} extra at ${seatPrice}${seatPer} each` : ""}
          </p>
          {team.trial_active && full && <p className="mt-1 text-[11px] text-emerald-800" data-testid="team-trial-seat-note">Pick a Team plan above to add more assistants.</p>}
          {extraNow && !full && <p className="mt-1 text-[11px] text-amber-700" data-testid="team-extra-seat-note">More assistants are {seatPrice}/{per} each, added to your Team bill right away (prorated for this {per}).</p>}
          <TeamReady members={team.members} />
          <HideQueueToggle key={team.members.map((m) => m.hide_queue).join()} initial={team.hide_queue} onChanged={load} />
          <ul>{team.members.map((m) => <MemberRow key={m.id} m={m} onChanged={load} />)}</ul>
          <AlertDialog open={confirmSeat} onOpenChange={setConfirmSeat}>
            <AlertDialogContent className="w-[calc(100%-1.5rem)] max-w-sm rounded-xl" data-testid="team-seat-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle>Add an extra seat for {seatPrice}/{per}?</AlertDialogTitle>
                <AlertDialogDescription>Your Team plan includes {team.seats} assistants. This one adds {seatPrice}/{per} to your bill, charged now for the rest of this {per}. Removing them stops the charge.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-2">
                <AlertDialogCancel className="rounded-full" data-testid="team-seat-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={add} className="rounded-full bg-[#0053DA] text-white hover:bg-[#0043B0]" data-testid="team-seat-confirm-button">Add seat</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      )}
      {team.is_team && <Leaderboard />}
      {team.is_team && <TeamActivity items={team.activity} />}
    </div>
  );
}
